// S5.4/F-03 — Pure translate/resize utilities extracted VERBATIM from the
// legacy DrawingLayer.tsx (no logic changes). DrawingLayer re-exports these
// for API compat; new code imports from here (or lib/drawing index).
import { computeTextBoxLayout } from "@vninvestcharts/engine-core";
import { replacePoint } from "./shared";
function translateDrawing(drawing, deltaX, deltaY, deltaIndex) {
    if (deltaX === 0 && deltaY === 0 && (deltaIndex == null || deltaIndex === 0)) {
        return drawing;
    }
    return {
        ...drawing,
        points: drawing.points.map((point) => ({
            x: point.x + deltaX,
            y: point.y + deltaY,
            ...(typeof point.xIndexFloat === "number" && Number.isFinite(point.xIndexFloat) && typeof deltaIndex === "number" && Number.isFinite(deltaIndex)
                ? { xIndexFloat: point.xIndexFloat + deltaIndex }
                : typeof point.xIndexFloat === "number" && Number.isFinite(point.xIndexFloat)
                    ? { xIndexFloat: point.xIndexFloat }
                    : {}),
            ...(typeof point.timeMsHint === "number" && Number.isFinite(point.timeMsHint) ? { timeMsHint: point.timeMsHint + deltaX } : {}),
            ...(typeof point.yPrice === "number" && Number.isFinite(point.yPrice) ? { yPrice: point.yPrice + deltaY } : {}),
        })),
        style: { ...drawing.style },
        fibLevels: drawing.fibLevels ? [...drawing.fibLevels] : undefined,
        riskReward: drawing.riskReward ? { ...drawing.riskReward } : undefined,
        updatedAt: Date.now(),
    };
}
export function translateDrawingsByIds(drawings, objectIds, deltaX, deltaY, deltaIndex) {
    if (deltaX === 0 && deltaY === 0 && (deltaIndex == null || deltaIndex === 0)) {
        return [...drawings];
    }
    const selectedObjectIdSet = new Set(objectIds);
    return drawings.map((drawing) => (selectedObjectIdSet.has(drawing.id)
        ? translateDrawing(drawing, deltaX, deltaY, deltaIndex)
        : drawing));
}
function calculatePositionRiskReward(type, startPoint, endPoint) {
    if (!startPoint || !endPoint) {
        return undefined;
    }
    if (type === "longPosition") {
        return {
            entry: startPoint.y,
            stop: Math.min(startPoint.y, endPoint.y),
            target: Math.max(startPoint.y, endPoint.y),
        };
    }
    if (type === "shortPosition") {
        return {
            entry: startPoint.y,
            stop: Math.max(startPoint.y, endPoint.y),
            target: Math.min(startPoint.y, endPoint.y),
        };
    }
    return undefined;
}
function constrainResizePoint(drawing, handleIndex, nextPoint) {
    switch (drawing.type) {
        case "hLine": {
            const anchorIndex = handleIndex === 0 ? 1 : 0;
            const anchorPoint = drawing.points[anchorIndex];
            const anchorY = anchorPoint?.y ?? nextPoint.y;
            const anchorYPrice = typeof anchorPoint?.yPrice === "number" && Number.isFinite(anchorPoint.yPrice)
                ? anchorPoint.yPrice
                : anchorY;
            return {
                x: nextPoint.x,
                y: anchorY,
                ...(typeof nextPoint.xIndexFloat === "number" && Number.isFinite(nextPoint.xIndexFloat) ? { xIndexFloat: nextPoint.xIndexFloat } : {}),
                ...(typeof nextPoint.timeMsHint === "number" && Number.isFinite(nextPoint.timeMsHint) ? { timeMsHint: nextPoint.timeMsHint } : {}),
                yPrice: anchorYPrice,
            };
        }
        case "vLine": {
            const anchorIndex = handleIndex === 0 ? 1 : 0;
            const anchorPoint = drawing.points[anchorIndex];
            const anchorX = anchorPoint?.x ?? nextPoint.x;
            const anchorTimeMsHint = typeof anchorPoint?.timeMsHint === "number" && Number.isFinite(anchorPoint.timeMsHint)
                ? anchorPoint.timeMsHint
                : anchorX;
            return {
                x: anchorX,
                y: nextPoint.y,
                timeMsHint: anchorTimeMsHint,
                ...(typeof anchorPoint?.xIndexFloat === "number" && Number.isFinite(anchorPoint.xIndexFloat) ? { xIndexFloat: anchorPoint.xIndexFloat } : {}),
                ...(typeof nextPoint.yPrice === "number" && Number.isFinite(nextPoint.yPrice) ? { yPrice: nextPoint.yPrice } : {}),
            };
        }
        default:
            return nextPoint;
    }
}
function resizeDrawing(drawing, handleIndex, nextPoint) {
    if (drawing.points.length === 0) {
        return drawing;
    }
    const targetIndex = Math.max(0, Math.min(handleIndex, drawing.points.length - 1));
    const constrainedPoint = constrainResizePoint(drawing, targetIndex, nextPoint);
    const currentPoint = drawing.points[targetIndex];
    const normalizedPoint = {
        x: constrainedPoint.x,
        y: constrainedPoint.y,
        ...(typeof constrainedPoint.xIndexFloat === "number" && Number.isFinite(constrainedPoint.xIndexFloat)
            ? { xIndexFloat: constrainedPoint.xIndexFloat }
            : typeof currentPoint?.xIndexFloat === "number" && Number.isFinite(currentPoint.xIndexFloat)
                ? { xIndexFloat: currentPoint.xIndexFloat }
                : {}),
        ...(typeof constrainedPoint.timeMsHint === "number" && Number.isFinite(constrainedPoint.timeMsHint)
            ? { timeMsHint: constrainedPoint.timeMsHint }
            : typeof currentPoint?.timeMsHint === "number" && Number.isFinite(currentPoint.timeMsHint)
                ? { timeMsHint: currentPoint.timeMsHint }
                : {}),
        ...(typeof constrainedPoint.yPrice === "number" && Number.isFinite(constrainedPoint.yPrice)
            ? { yPrice: constrainedPoint.yPrice }
            : typeof currentPoint?.yPrice === "number" && Number.isFinite(currentPoint.yPrice)
                ? { yPrice: currentPoint.yPrice }
                : {}),
    };
    const updated = replacePoint(drawing, targetIndex, normalizedPoint);
    const resized = {
        ...updated,
        style: { ...drawing.style },
        fibLevels: drawing.fibLevels ? [...drawing.fibLevels] : undefined,
        riskReward: drawing.riskReward ? { ...drawing.riskReward } : undefined,
        updatedAt: Date.now(),
    };
    if (drawing.type === "longPosition" || drawing.type === "shortPosition") {
        return {
            ...resized,
            riskReward: calculatePositionRiskReward(drawing.type, resized.points[0], resized.points[1]),
        };
    }
    return resized;
}
export function resizeDrawingsByIds(drawings, objectIds, handleIndex, nextPoint, textBoxEdge) {
    if (objectIds.length === 0) {
        return [...drawings];
    }
    const selectedObjectIdSet = new Set(objectIds);
    return drawings.map((drawing) => {
        if (!selectedObjectIdSet.has(drawing.id)) {
            return drawing;
        }
        if (handleIndex >= 100 && textBoxEdge && (drawing.type === "callout" || drawing.type === "text" || drawing.type === "priceLabel")) {
            return resizeTextBoxEdge(drawing, handleIndex, textBoxEdge);
        }
        return resizeDrawing(drawing, handleIndex, nextPoint);
    });
}
function resizeTextBoxEdge(drawing, handleIndex, p) {
    const fontSize = drawing.style?.fontSize ?? 12;
    const { boxWidth: curW, bgHeight: curH } = computeTextBoxLayout(drawing.text ?? "", drawing.style?.textBoxWidth, drawing.style?.textBoxHeight, fontSize);
    const MIN_W = 30;
    const MIN_H = 20;
    const { mousePixelX: mx, mousePixelY: my, anchorPixelX: ax, anchorPixelY: ay, xScaleInvert, yScaleInvert, xScaleInvertToIndex } = p;
    let newAX = ax;
    let newAY = ay;
    let newW = curW;
    let newH = curH;
    switch (handleIndex) {
        case 100:
            newAX = mx;
            newAY = my;
            newW = curW + (ax - mx);
            newH = curH + (ay - my);
            break;
        case 101:
            newAY = my;
            newH = curH + (ay - my);
            break;
        case 102:
            newAY = my;
            newW = mx - ax;
            newH = curH + (ay - my);
            break;
        case 103:
            newW = mx - ax;
            break;
        case 104:
            newW = mx - ax;
            newH = my - ay;
            break;
        case 105:
            newH = my - ay;
            break;
        case 106:
            newAX = mx;
            newW = curW + (ax - mx);
            newH = my - ay;
            break;
        case 107:
            newAX = mx;
            newW = curW + (ax - mx);
            break;
    }
    newW = Math.max(MIN_W, newW);
    newH = Math.max(MIN_H, newH);
    const result = {
        ...drawing,
        style: {
            ...drawing.style,
            textBoxWidth: Math.round(newW),
            textBoxHeight: Math.round(newH),
        },
        updatedAt: Date.now(),
    };
    if (handleIndex <= 102 || handleIndex === 106 || handleIndex === 107) {
        const newX = xScaleInvert(newAX).getTime();
        const newY = yScaleInvert(newAY);
        result.points = [...drawing.points];
        result.points[0] = {
            ...drawing.points[0],
            x: newX,
            y: newY,
            ...(typeof drawing.points[0]?.xIndexFloat === "number"
                ? { xIndexFloat: xScaleInvertToIndex?.(newAX) ?? drawing.points[0].xIndexFloat }
                : {}),
            ...(typeof drawing.points[0]?.yPrice === "number" ? { yPrice: newY } : {}),
        };
    }
    return result;
}
